/**
 * useTaskReminders · Snooze v2.0.
 *
 * Por defecto cada user ve sólo sus reminders (RLS).
 * El componente del drawer usa `useRemindersForTask(taskId)` para mostrar
 * los reminders del user actual sobre esa task puntual.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useSupabase } from '@/hooks/useSupabase';

import * as remindersApi from '../api/taskReminders';

export const REMINDER_KEYS = {
  mine: ['core', 'task_reminders', 'mine'] as const,
  byTask: (taskId: string) => ['core', 'task_reminders', 'task', taskId] as const,
};

export function useMyReminders() {
  return useQuery({
    queryKey: REMINDER_KEYS.mine,
    queryFn: remindersApi.listMyReminders,
    staleTime: 60_000,
  });
}

export function useRemindersForTask(taskId: string | null | undefined) {
  return useQuery({
    queryKey: REMINDER_KEYS.byTask(taskId ?? ''),
    queryFn: () => remindersApi.listRemindersForTask(taskId!),
    enabled: !!taskId,
    staleTime: 30_000,
  });
}

export function useCreateReminder(taskId: string) {
  const qc = useQueryClient();
  const { session } = useSupabase();
  const userId = session?.user?.id;

  return useMutation({
    mutationFn: async (input: { remind_at: string; note?: string | null }) => {
      if (!userId) throw new Error('No hay sesión');
      await remindersApi.createReminder({ task_id: taskId, ...input }, userId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REMINDER_KEYS.byTask(taskId) });
      qc.invalidateQueries({ queryKey: REMINDER_KEYS.mine });
      toast.success('Recordatorio creado');
    },
    onError: (e: Error) => toast.error('No se pudo crear el recordatorio', { description: e.message }),
  });
}

export function useDeleteReminder(taskId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => remindersApi.deleteReminder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REMINDER_KEYS.byTask(taskId) });
      qc.invalidateQueries({ queryKey: REMINDER_KEYS.mine });
      toast.success('Recordatorio eliminado');
    },
    onError: (e: Error) => toast.error('No se pudo eliminar', { description: e.message }),
  });
}
